import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage';
import { faImages } from '@fortawesome/pro-regular-svg-icons/faImages';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { faVideoPlus } from '@fortawesome/pro-regular-svg-icons/faVideoPlus';
import FloorPlanIcon from '@propertypal/shared/src/resources/icons/property/floorplan-icon-black.svg';
import SitemapIcon from '@propertypal/shared/src/resources/icons/property/sitemap-black.svg';
import { Property, Tour } from '@propertypal/shared/src/types/property';
import { getGalleryImages } from '@propertypal/shared/src/utils/property';
import { GalleryItem, getSiteMaps } from '@propertypal/shared/src/utils/property/getGalleryImages';
import getPropertyFloorPlan from '@propertypal/shared/src/utils/property/getPropertyFloorPlan';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import useMediaQuery from '@propertypal/web-ui/src/media-query/useMediaQuery';
import ModalWrapper from '@propertypal/web-ui/src/modal/ModalWrapper';
import ImageGrid from '@propertypal/web-ui/src/property/ImageGrid';
import PropertySlider from '@propertypal/web-ui/src/property/PropertySlider';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import AdBox from '../ads/AdBox';
import { PROPERTY_IMAGE_LOCATOR } from './PropertyDetails';
import {
  CloseButton,
  Container,
  Header,
  TabContent,
  Tab,
  TabText,
  SliderBox,
  slideshowMax,
  AdContainer,
} from './PropertySlideshowModal.style';

interface Props {
  show: boolean;
  property: Property;
  imageIndex: number;
  setImageIndex: (index: number) => void;
  onClose: () => void;
  onTabClick: (tab: string, index?: number) => void;
  url: string;
  imageLocator: string;
  adSuffix: string;
  hasSitemap: boolean;
}

const IFRAME_SANDBOX = [
  'allow-same-origin',
  'allow-presentation',
  'allow-scripts',
  'allow-storage-access-by-user-activation',
  'allow-orientation-lock allow-forms',
];

const AD_SIZE = [[320, 50]];

const getTabFromUrl = (url: string) => {
  // also remove any query-strings
  return url
    .replace(/\/photo-\d+/, '')
    ?.split('/')[3]
    ?.split('?')[0];
};

const getFloorPlanIndex = (activeTab: string) => {
  if (activeTab.startsWith('floor-plans')) {
    const [, , index] = activeTab.split('-');

    return index ? parseInt(index, 10) - 1 : 0;
  }

  return 0;
};

const PropertySlideshowModal: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const firstTabButton = useRef<HTMLButtonElement>(null);
  const images = getGalleryImages(props.property, props.imageLocator, true, true);
  const sitemaps = getSiteMaps(props.property, PROPERTY_IMAGE_LOCATOR[2]);
  const floorPlans = getPropertyFloorPlan(props.property, PROPERTY_IMAGE_LOCATOR[2]);
  const isMobile = useMediaQuery({ query: `(${slideshowMax})` });
  const activeTab = getTabFromUrl(props.url);
  const floorPlanIndex = getFloorPlanIndex(activeTab);

  const setFloorPlanIndex = (index: number) => {
    if (index === 0) {
      props.onTabClick(`floor-plans`);
    } else {
      props.onTabClick(`floor-plans-${index + 1}`);
    }
  };

  const handleTourTabClick = (tour: Tour, index: number) => {
    if (tour.insecure) {
      window.open(tour.url, tour.label, 'popup');
    } else {
      props.onTabClick(`tour${index + 1}`);
    }
  };

  const handleImageClick = (image: GalleryItem) => {
    if (image.type === 'webview' && image.insecure) {
      window.open(image.link, image.text, 'popup');
    } else if (image.type === 'webview') {
      props.onTabClick(`tour${image.tourIndex}`);
    } else if (image.type === 'video') {
      props.onTabClick('video');
    }
  };

  useEffect(() => {
    if (props.show) {
      firstTabButton.current?.focus();
      // Allows arrow keys to work for cycling through images
      window.scrollTo(0, 0);
    }
  }, [props.show]);

  return (
    <ModalWrapper scrollSelector="#image-grid" show={props.show} onClose={props.onClose}>
      <Container data-testid="slideshow-modal">
        <Header>
          <TabContent>
            {images.length > 0 && (
              <>
                <Tab
                  active={activeTab === 'slideshow'}
                  ref={firstTabButton}
                  onClick={() => props.onTabClick('slideshow')}
                  data-testid="slideshowTabButton"
                >
                  <TabText>
                    <FontAwesomeIcon icon={faImage} />
                    {isMobile ? '' : ' Slideshow'}
                  </TabText>
                </Tab>

                <Tab
                  active={activeTab === 'gallery'}
                  onClick={() => props.onTabClick('gallery')}
                  data-testid="galleryTabButton"
                >
                  <TabText>
                    <FontAwesomeIcon icon={faImages} />
                    {isMobile ? '' : ' Gallery'}
                  </TabText>
                </Tab>
              </>
            )}

            {props.property['360Tours']?.map((tour, index) => (
              <Tab
                key={`tour${index + 1}`}
                active={activeTab === `tour${index + 1}`}
                onClick={() => handleTourTabClick(tour, index)}
                data-testid="tourTab"
              >
                <TabText>
                  <FontAwesomeIcon icon={tour.type === 'video' ? faVideo : faVideoPlus} />
                  {isMobile ? '' : tour.type === 'video' ? 'Video' : tour.label}
                </TabText>
              </Tab>
            ))}

            {!!props.property.videos?.length && (
              <Tab active={activeTab === 'video'} onClick={() => props.onTabClick('video')} data-testid="videoTab">
                <TabText>
                  <FontAwesomeIcon icon={faVideo} />
                  {isMobile ? '' : ' Video'}
                </TabText>
              </Tab>
            )}

            {!!floorPlans?.length && (
              <Tab active={activeTab.startsWith('floor-plans')} onClick={() => props.onTabClick('floor-plans')}>
                <TabText>
                  <FloorPlanIcon />
                  {isMobile ? '' : 'Floor Plans'}
                </TabText>
              </Tab>
            )}

            {props.hasSitemap && (
              <Tab
                active={activeTab === 'sitemap'}
                onClick={() => props.onTabClick('sitemap')}
                data-testid="sitemapTabButton"
              >
                <TabText as="span">
                  <SitemapIcon />
                  {isMobile ? '' : ' Site Map'}
                </TabText>
              </Tab>
            )}
          </TabContent>

          <CloseButton
            aria-label="Close property image modal"
            data-testid="closeImageModal"
            onClick={props.onClose}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} color={theme.white} style={{ fontSize: 30 }} />
          </CloseButton>
        </Header>

        {/* prevent accessing sitemap image if not a featured development */}
        {activeTab === 'sitemap' && props.hasSitemap && (
          <SliderBox data-testid="sitemap-slider">
            <PropertySlider
              alt={props.property.displayAddress}
              images={sitemaps}
              imageIndex={props.imageIndex}
              setImageIndex={props.setImageIndex}
              fullscreen
              sitemap
              maxHeight="100%"
            />
          </SliderBox>
        )}

        {activeTab.startsWith('floor-plans') && floorPlans?.length && (
          <SliderBox data-testid="floor-plans-slider">
            <PropertySlider
              alt={props.property.displayAddress}
              images={floorPlans}
              imageIndex={floorPlanIndex}
              setImageIndex={setFloorPlanIndex}
              fullscreen
              floorPlans
              maxHeight="100%"
            />
          </SliderBox>
        )}

        {activeTab === 'slideshow' && (
          <SliderBox>
            <PropertySlider
              alt={props.property.addressLine1}
              images={images}
              imageIndex={props.imageIndex}
              setImageIndex={props.setImageIndex}
              onClick={handleImageClick}
              fullscreen
              maxHeight="100%"
            />
          </SliderBox>
        )}

        {props.property['360Tours']?.map((tour, index) => {
          if (activeTab === `tour${index + 1}`) {
            return (
              <iframe
                data-testid="propertyTour"
                title={tour.label}
                key={tour.url}
                src={`${tour.url.replace('rel=0', 'autoplay=1&rel=0')}`}
                style={{ minHeight: '100%', maxHeight: '100%', border: 'none' }}
                sandbox={IFRAME_SANDBOX.join(' ')}
              />
            );
          }

          return null;
        })}

        {!!props.property.videos?.length && activeTab === 'video' && (
          <video
            autoPlay
            playsInline
            data-testid="propertyVideo"
            preload="auto"
            controls
            poster={props.property.videos[0].stillImage?.url}
            style={{ minHeight: '100%', maxHeight: '100%' }}
          >
            <source src={props.property.videos[0].url} type="video/mp4" />
          </video>
        )}

        <ImageGrid
          containerStyle={{
            visibility: activeTab === 'gallery' ? 'visible' : 'hidden',
          }}
          property={props.property}
          imageLocator={props.imageLocator}
          onImageClick={(index) => props.onTabClick('slideshow', index)}
          hasSitemap={props.hasSitemap}
        />
      </Container>

      {!props.property.featuredDevelopmentContent && ['slideshow'].includes(activeTab) && (
        <AdContainer>
          <AdBox
            id={`PDPmediabox_${props.adSuffix}`}
            config="PDPmediabox"
            hideOver={780}
            sizes={AD_SIZE}
            containerStyle={{ width: 320 }}
          />
        </AdContainer>
      )}
    </ModalWrapper>
  );
};

export default PropertySlideshowModal;
